/**
 * Real-time presence (doc 147 §8.4, §9 Phase 4).
 *
 * Avatars of teammates currently viewing the same entity (the Linear / Notion
 * pattern). Presentational — `viewers` is injected; the heartbeat write +
 * subscription live in the container.
 */
import type { PresenceViewer } from '../state/atlasApiTypes.js';

/** A presence heartbeat is stale once older than this (matches the backend). */
export const PRESENCE_STALE_MS = 90_000;

export interface PresenceLayerProps {
  viewers: PresenceViewer[];
  /** Current uid — excluded from the "others viewing" list. */
  selfUid: string;
  /** Injectable clock for deterministic staleness in tests. */
  now?: number;
}

/** Two initials from a display name, for the avatar. */
export function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function PresenceLayer({
  viewers,
  selfUid,
  now = Date.now(),
}: PresenceLayerProps): React.JSX.Element | null {
  const others = viewers.filter(
    (v) => v.uid !== selfUid && now - v.heartbeatAt <= PRESENCE_STALE_MS,
  );
  if (others.length === 0) return null;

  return (
    <div
      className="presence-layer"
      role="status"
      aria-label={`${others.length} other viewer${others.length === 1 ? '' : 's'}`}
    >
      {others.slice(0, 5).map((viewer) => (
        <span
          key={viewer.uid}
          className="presence-avatar"
          title={`${viewer.displayName} is viewing this`}
        >
          {initialsOf(viewer.displayName)}
        </span>
      ))}
      {others.length > 5 && <span className="presence-avatar">+{others.length - 5}</span>}
    </div>
  );
}
